import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from 'react';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';

// Context for dropdown state management
interface DropdownContextValue {
  isOpen: boolean;
  close: () => void;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdownContext() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('Dropdown components must be used within a Dropdown component');
  }
  return context;
}

// Types
export type DropdownPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

export interface DropdownProps {
  children: React.ReactNode;
  trigger: React.ReactNode;
  placement?: DropdownPlacement;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  disabled?: boolean;
  closeOnSelect?: boolean;
}

export interface MenuProps {
  children: React.ReactNode;
  className?: string;
}

export interface MenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  shortcut?: string;
  danger?: boolean;
  className?: string;
}

export interface MenuDividerProps {
  className?: string;
}

export interface MenuLabelProps {
  children: React.ReactNode;
  className?: string;
}

// Placement styles
const placementStyles: Record<DropdownPlacement, string> = {
  'bottom-start': 'top-full left-0 mt-1',
  'bottom-end': 'top-full right-0 mt-1',
  'top-start': 'bottom-full left-0 mb-1',
  'top-end': 'bottom-full right-0 mb-1',
};

// Main Dropdown component
export function Dropdown({
  children,
  trigger,
  placement = 'bottom-start',
  className,
  triggerClassName,
  menuClassName,
  disabled = false,
  closeOnSelect = true,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        close();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, close]);

  return (
    <DropdownContext.Provider value={{ isOpen, close: closeOnSelect ? close : () => {} }}>
      <div ref={dropdownRef} className={clsx('relative inline-block', className)}>
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          disabled={disabled}
          aria-haspopup="true"
          aria-expanded={isOpen}
          className={clsx(
            'inline-flex items-center justify-center',
            disabled && 'opacity-50 cursor-not-allowed',
            triggerClassName
          )}
        >
          {trigger}
        </button>

        {isOpen && (
          <div
            role="menu"
            aria-orientation="vertical"
            className={clsx(
              'absolute z-50 min-w-[160px] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 focus:outline-none',
              placementStyles[placement],
              menuClassName
            )}
          >
            {children}
          </div>
        )}
      </div>
    </DropdownContext.Provider>
  );
}

// Menu container (for use inside Dropdown)
export function Menu({ children, className }: MenuProps) {
  return <div className={clsx('py-1', className)}>{children}</div>;
}

// Menu item
export function MenuItem({
  children,
  onClick,
  disabled = false,
  icon,
  shortcut,
  danger = false,
  className,
}: MenuItemProps) {
  const { close } = useDropdownContext();

  const handleClick = () => {
    if (!disabled) {
      onClick?.();
      close();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={clsx(
        'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
        'focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700',
        disabled
          ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
          : danger
          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700',
        className
      )}
    >
      {icon && <span className="flex-shrink-0 w-4 h-4">{icon}</span>}
      <span className="flex-1">{children}</span>
      {shortcut && (
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{shortcut}</span>
      )}
    </button>
  );
}

// Menu divider
export function MenuDivider({ className }: MenuDividerProps) {
  return (
    <div
      role="separator"
      className={clsx('my-1 border-t border-gray-200 dark:border-gray-700', className)}
    />
  );
}

// Menu label
export function MenuLabel({ children, className }: MenuLabelProps) {
  return (
    <div
      className={clsx(
        'px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider',
        className
      )}
    >
      {children}
    </div>
  );
}

// Action dropdown with button trigger
export interface ActionDropdownProps {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  placement?: DropdownPlacement;
  variant?: 'default' | 'primary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

const buttonVariants = {
  default: 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700',
  primary: 'bg-primary-600 border border-primary-600 text-white hover:bg-primary-700',
  ghost: 'bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700',
};

const buttonSizes = {
  sm: 'px-2.5 py-1.5 text-sm rounded-md',
  md: 'px-3 py-2 text-sm rounded-lg',
  lg: 'px-4 py-2.5 text-base rounded-lg',
};

export function ActionDropdown({
  label,
  icon,
  children,
  placement = 'bottom-end',
  variant = 'default',
  size = 'md',
  disabled = false,
  className,
}: ActionDropdownProps) {
  return (
    <Dropdown
      placement={placement}
      disabled={disabled}
      trigger={
        <span
          className={clsx(
            'inline-flex items-center gap-2 font-medium transition-colors',
            buttonVariants[variant],
            buttonSizes[size],
            className
          )}
        >
          {icon}
          {label && <span>{label}</span>}
          <ChevronDown className="h-4 w-4" />
        </span>
      }
    >
      {children}
    </Dropdown>
  );
}
